/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { BaseService } from 'apps/console-core/src/Services/_base';
import { HttpResponse } from 'apps/console-core/src/Services/http';
import { Observable, of, switchMap } from 'rxjs';
import {
  EntityScvCustomer,
  EntityScvCustomers,
  EntityScvExport,
  EntityScvExportResult,
  EntityScvExports,
  EntityScvSchema,
} from './Entities/entities';
import { SingleCustomerViewServiceConfiguration } from './SingleCustomerView.service.config';
import {
  SingleCustomerViewServiceCreateExportDto,
  SingleCustomerViewServiceDeleteCustomerDto,
  SingleCustomerViewServiceGetCustomerDto,
  SingleCustomerViewServiceGetCustomersDto,
  SingleCustomerViewServiceGetExportDto,
} from './SingleCustomerView.service.dto';

/**
 * The single customer view service.
 * @export
 * @class SingleCustomerViewService
 */
export class SingleCustomerViewService extends BaseService {
  /**
   * Creates an instance of SingleCustomerViewService.
   * @param {SingleCustomerViewServiceConfiguration} configuration The configuration options to accept.
   * @memberof SingleCustomerViewService
   */
  public constructor(configuration: SingleCustomerViewServiceConfiguration) {
    super(configuration);
  }

  /**
   * Creates base api url for the Single Customer View service.
   * @returns {string | null} The base api url for the Single Customer View service.
   * @memberof SingleCustomerViewService
   */
  getBaseServiceUrl(): string | null {
    // This service is deployed in multiple regions and
    // needs to use the tenant's region.
    return this.getServiceUrlByTenant(`scv`);
  }

  /**
   * Creates api url for customers.
   * @param {string} [customerId] If present, append customer ID as the URL slug.
   * @returns {string | null} The api url for a customer or customers.
   */
  public createCustomersUrl(customerId?: string): string | null {
    const baseUrl = this.getBaseServiceUrl();

    if (!baseUrl) {
      return null;
    }

    return customerId ? `${baseUrl}/customers/${customerId}` : `${baseUrl}/customers`;
  }

  /**
   * Creates api url for exports.
   * @param {string} [exportId] If present, api url will be for a specified export.
   * @returns {string | null} The api url for an export or exports.
   */
  public createExportUrl(exportId?: string): string | null {
    const baseUrl = this.getBaseServiceUrl();

    if (!baseUrl) {
      return null;
    }

    return exportId ? `${baseUrl}/export/customers/${exportId}` : `${baseUrl}/export/customers`;
  }

  /**
   * Creates api url for schema.
   * @returns {string | null} The api url for a schema.
   */
  public createSchemaUrl(): string | null {
    const baseUrl = this.getBaseServiceUrl();

    if (!baseUrl) {
      return null;
    }

    return `${baseUrl}/schema`;
  }

  /**
   * Attempt to get a list of customers for a segment.
   * @param {SingleCustomerViewServiceGetCustomersDto} dto The DTO to get the customers of a segment.
   * @returns {Observable<EntityScvCustomers | null>} The observable of customers for a segment.
   * @memberof SingleCustomerViewService
   */
  public getCustomers(dto: SingleCustomerViewServiceGetCustomersDto): Observable<EntityScvCustomers | null> {
    const { pageAfter, pageBefore, pageSize, searchParams, searchValue, segment } = dto;

    const queryParams: Array<string> = [];

    // Note: if either of these conditions is not met the API will throw an error.
    if (segment) {
      queryParams.push(`segment=${segment}`);
    } else if (searchParams && searchValue) {
      queryParams.push(`search[${searchParams.join(`][`)}]=${searchValue}`);
    }

    if (pageSize) {
      queryParams.push(`${encodeURIComponent('page[size]')}=${pageSize}`);
    }

    if (pageBefore) {
      queryParams.push(`${encodeURIComponent(`page[before]`)}=${pageBefore}`);
    }

    if (pageAfter) {
      queryParams.push(`${encodeURIComponent(`page[after]`)}=${pageAfter}`);
    }

    // This returns all available properties of the customers.
    // See `ScvCustomer.entity.ts`
    queryParams.push(`retAllProps=1`);

    const baseCustomersUrl = this.createCustomersUrl();
    if (!baseCustomersUrl) {
      // eslint-disable-next-line no-console
      console.error(`Undefined tenant`);
      return of(null);
    }

    const requestUrl = this.appendQueryParamsToUrl(queryParams, baseCustomersUrl);
    return this.httpService
      .get<unknown, HttpResponse>(requestUrl, {
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<EntityScvCustomers> => {
          return this._handleResponseError<EntityScvCustomers>(value);
        }),
      );
  }

  /**
   * Attempt to get the details of a customer.
   * @param {SingleCustomerViewServiceGetCustomerDto} dto The DTO to get the customer details.
   * @returns {Observable<EntityScvCustomer | null>} The observable of customer details.
   * @memberof SingleCustomerViewService
   */
  public getCustomer(dto: SingleCustomerViewServiceGetCustomerDto): Observable<EntityScvCustomer | null> {
    const { customerId } = dto;

    const queryParams: Array<string> = [];

    // This returns all available properties of the customer.
    // See `ScvCustomer.entity.ts`
    queryParams.push(`retAllProps=1`);

    const baseCustomerUrl = this.createCustomersUrl(customerId);
    if (!baseCustomerUrl) {
      // eslint-disable-next-line no-console
      console.error(`Undefined tenant`);
      return of(null);
    }

    const requestUrl = this.appendQueryParamsToUrl(queryParams, baseCustomerUrl);

    return this.httpService
      .get<unknown, HttpResponse>(requestUrl, {
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<EntityScvCustomer> => {
          return this._handleResponseError<EntityScvCustomer>(value);
        }),
      );
  }

  /**
   * Attempt to delete a customer.
   * @param {SingleCustomerViewServiceDeleteCustomerDto} dto The DTO to delete the customer.
   * @returns {Observable<void>} The observable of customer details.
   * @memberof SingleCustomerViewService
   */
  public deleteCustomer(dto: SingleCustomerViewServiceDeleteCustomerDto): Observable<EntityScvCustomer | null> {
    const { customerId } = dto;

    const baseCustomerUrl = this.createCustomersUrl(customerId);
    if (!baseCustomerUrl) {
      // eslint-disable-next-line no-console
      console.error(`Undefined tenant`);
      return of(null);
    }

    return this.httpService
      .delete<unknown, HttpResponse>(baseCustomerUrl, {
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<EntityScvCustomer> => {
          return this._handleResponseError<EntityScvCustomer>(value);
        }),
      );
  }

  /**
   * Attempt to get a specific export.
   * @param {SingleCustomerViewServiceGetExportDto} dto The DTO to get the specified export.
   * @returns {Observable<EntityScvExport | null>} The observable of the specified export.
   * @memberof SingleCustomerViewService
   */
  public getExport(dto: SingleCustomerViewServiceGetExportDto): Observable<EntityScvExport | null> {
    const { exportId } = dto;

    const requestUrl = this.createExportUrl(exportId);
    if (!requestUrl) {
      return of(null);
    }

    return this.httpService
      .get<unknown, HttpResponse>(requestUrl, {
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<EntityScvExport> => {
          return this._handleResponseError<EntityScvExport>(value);
        }),
      );
  }

  /**
   * Attempt to get a list of exports for the organisation.
   * @returns {Observable<EntityScvExports | null>} The observable of exports.
   * @memberof SingleCustomerViewService
   */
  public getExports(): Observable<EntityScvExports | null> {
    const requestUrl = this.createExportUrl();
    if (!requestUrl) {
      return of(null);
    }

    return this.httpService
      .get<unknown, HttpResponse>(requestUrl, {
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<EntityScvExports> => {
          return this._handleResponseError<EntityScvExports>(value);
        }),
      );
  }

  /**
   * Attempt to create an export for the organisation.
   * @param {SingleCustomerViewServiceCreateExportDto} dto The DTO to generate an export.
   * @returns {Observable<EntityScvExportResult | null>} The observable of the export result.
   * @memberof SingleCustomerViewService
   */
  public createExport(dto: SingleCustomerViewServiceCreateExportDto): Observable<EntityScvExportResult | null> {
    const { segment } = dto;

    const requestUrl = this.createExportUrl();
    if (!requestUrl) {
      return of(null);
    }

    return this.httpService
      .post<unknown, HttpResponse>(requestUrl, {
        ...this.getSessionRequestOptions(),
        body: {
          segment,
        },
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<EntityScvExportResult> => {
          return this._handleResponseError<EntityScvExportResult>(value);
        }),
      );
  }

  /**
   * Attempt to get the schema for an organization.
   * @returns {Observable<EntityScvSchema | null>} The observable of schema.
   * @memberof SingleCustomerViewService
   */
  public getSchema(): Observable<EntityScvSchema | null> {
    const requestUrl = this.createSchemaUrl();
    if (!requestUrl) {
      return of(null);
    }

    return this.httpService
      .get<unknown, HttpResponse>(requestUrl, {
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<EntityScvSchema> => {
          return this._handleResponseError<EntityScvSchema>(value);
        }),
      );
  }
}
